import React, { useId } from 'react';
import SpeakerIcon from './SpeakerIcon';
import { SPEAKER_ROLE, resolveSpeaker } from './speakerRoles';

/** 話者の凡例の文言 (SCR-D-001 DP-D-007。固定文言) */
export const SPEAKER_LEGEND_TEXT = {
    agent: 'エージェント（青・実線）',
    candidate: '求職者（Teal・破線）',
};

/** 話者を判定できなかった発話の文言 (SCR-D-001 DP-D-005) */
export const UNKNOWN_SPEAKER_MESSAGE = 'この発話の話者を判定できませんでした。';

/**
 * 役割の判定が不確実であることの文言 (SCR-D-001 DP-D-009)。
 * 「修正してください」「修正できます」と書かない (話者ラベル修正は 10/15 に含めないため。#889 J3)
 */
export const LOW_CONFIDENCE_MESSAGE = 'エージェントと求職者の判定が不確実です。話者ラベルをご確認ください。';

/**
 * 話者分離済みの文字起こし (SCR-D-001 DP-D-001・005・006・007・009)。
 *
 * 発話をカード風のブロックで並べ、ブロックごとに役割のアイコンとラベルを出す。
 * 各ブロックは Tab で移動でき、読み上げでは役割のラベルを名前の先頭に置く (SCR-D-001 §7.2)。
 * `aria-label` で役割だけを名前にすると本文が読まれなくなるため、ラベルと本文を
 * `aria-labelledby` で並べて名前にする。
 *
 * - DP-D-005: `role` が `unknown`（または想定外の値）の発話にだけ、Warning のアイコンと文言を添える。
 *   **API の `uncertain` では出さない**（`uncertain` は agent／candidate の発話にも付くため。1.6）
 * - DP-D-009: `roleConfidence` が `low` のとき、凡例の直下にバナーを 1 つ出す
 *
 * @param {{
 *   utterances: Array<{index: number, role: string, text: string}>,
 *   roleConfidence?: string,
 * }} props
 */
function DiarizedTranscript({ utterances, roleConfidence }) {
    const idPrefix = useId();
    return (
        <div className="diar-transcript">
            <p className="diar-legend" data-testid="diar-legend">
                <span className="diar-legend-mark diar-legend-mark--agent" aria-hidden="true">■</span>
                {' '}{SPEAKER_LEGEND_TEXT.agent}{' '}
                <span className="diar-legend-mark diar-legend-mark--candidate" aria-hidden="true">■</span>
                {' '}{SPEAKER_LEGEND_TEXT.candidate}
            </p>
            {roleConfidence === 'low' && (
                <p className="diar-warning diar-warning--banner" data-testid="diar-low-confidence">
                    <SpeakerIcon name="triangle-alert" />
                    <span>{LOW_CONFIDENCE_MESSAGE}</span>
                </p>
            )}
            <ol className="diar-utterance-list">
                {utterances.map((utterance) => {
                    const speaker = resolveSpeaker(utterance.role);
                    const labelId = `${idPrefix}-label-${utterance.index}`;
                    const textId = `${idPrefix}-text-${utterance.index}`;
                    const warningId = `${idPrefix}-warning-${utterance.index}`;
                    const isUnknown = speaker.role === SPEAKER_ROLE.unknown;
                    // 印の文言も名前に含める（Tab で移ったときに、判定できなかったことが読まれるように）
                    const labelledBy = isUnknown ? `${labelId} ${textId} ${warningId}` : `${labelId} ${textId}`;
                    return (
                        <li
                            key={utterance.index}
                            className={`diar-utterance diar-utterance--${speaker.role}`}
                            data-role={speaker.role}
                            tabIndex={0}
                            aria-labelledby={labelledBy}
                        >
                            <span className="diar-utterance-speaker">
                                <SpeakerIcon name={speaker.icon} />
                                <span id={labelId} className="diar-utterance-label">{speaker.label}</span>
                            </span>
                            <span id={textId} className="diar-utterance-text">{utterance.text}</span>
                            {isUnknown && (
                                <span className="diar-warning" data-testid="diar-unknown-speaker">
                                    <SpeakerIcon name="triangle-alert" />
                                    <span id={warningId}>{UNKNOWN_SPEAKER_MESSAGE}</span>
                                </span>
                            )}
                        </li>
                    );
                })}
            </ol>
        </div>
    );
}

export default DiarizedTranscript;
